import CloseIcon from "@mui/icons-material/Close";
import KeyIcon from "@mui/icons-material/Key";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { isSafeUrl } from "../../../utils";
import { useEmployeeTerminology } from "../../../utils/terminology";
import {
  checkComposioStatus,
  connectComposioWithApiKey,
  fetchComposioApps,
  getComposioAuthFields,
  installGenericComposioIntegration,
  startComposioConnect,
} from "../api";

interface ComposioApp {
  name: string;
  slug: string;
  composioSlug: string; // Original Composio slug for API calls
  description: string;
  logo: string;
  categories: string[];
  authSchemes: string[];
  authType: "oauth" | "api_key";
  supportsOAuth: boolean;
  supportsApiKey: boolean;
}

interface AuthField {
  name: string;
  displayName: string;
  type: string;
  description: string;
  required: boolean;
}

interface ComposioCatalogDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  getToken: () => Promise<string>;
  onSuccess: () => void;
  onError: (error: string) => void;
}

export default function ComposioCatalogDialog({
  open,
  onClose,
  companyId,
  getToken,
  onSuccess,
  onError,
}: ComposioCatalogDialogProps) {
  const { employee: employeeText, employees: employeesText } = useEmployeeTerminology();
  const [apps, setApps] = useState<ComposioApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedApp, setSelectedApp] = useState<ComposioApp | null>(null);
  const [instanceName, setInstanceName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectedAccountId, setConnectedAccountId] = useState<string | null>(
    null
  );
  const [connectionError, setConnectionError] = useState("");
  const [authFields, setAuthFields] = useState<AuthField[]>([]);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [loadingAuthFields, setLoadingAuthFields] = useState(false);

  // Load apps when dialog opens
  useEffect(() => {
    if (open) {
      loadApps();
    }
  }, [open]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter apps on search change
  useEffect(() => {
    if (open && debouncedSearch !== "") {
      loadApps(debouncedSearch);
    } else if (open && debouncedSearch === "" && apps.length === 0) {
      loadApps();
    }
  }, [debouncedSearch]);

  const loadApps = async (search?: string) => {
    setLoading(true);
    try {
      const result = await fetchComposioApps(getToken, undefined, search);
      setApps(result.apps || []);
    } catch (err: any) {
      console.error("Error loading Composio apps:", err);
      onError(
        err.response?.data?.error || err.message || "Failed to load apps"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSelectApp = async (app: ComposioApp) => {
    setSelectedApp(app);
    setInstanceName(app.name);
    setApiKey("");
    setCredentials({});
    setAuthFields([]);
    setIsConnected(false);
    setConnectedAccountId(null);
    setConnectionError("");

    // If it's an API key app, fetch the required auth fields
    if (app.authType === "api_key") {
      try {
        setLoadingAuthFields(true);
        const result = await getComposioAuthFields(app.composioSlug, getToken);
        if (result.fields && result.fields.length > 0) {
          setAuthFields(result.fields);
          // Initialize credentials object with empty values
          const initialCredentials: Record<string, string> = {};
          result.fields.forEach((field: AuthField) => {
            initialCredentials[field.name] = "";
          });
          setCredentials(initialCredentials);
        }
      } catch (err) {
        console.error("Error fetching auth fields:", err);
        // If we can't get fields, fall back to single API key field
        setAuthFields([]);
      } finally {
        setLoadingAuthFields(false);
      }
    }
  };

  const handleBack = () => {
    setSelectedApp(null);
    setInstanceName("");
    setApiKey("");
    setCredentials({});
    setAuthFields([]);
    setIsConnected(false);
    setConnectedAccountId(null);
    setConnectionError("");
  };

  const handleOAuthConnect = useCallback(async () => {
    if (!selectedApp) return;

    setConnecting(true);
    setConnectionError("");

    try {
      const result = await startComposioConnect(
        companyId,
        selectedApp.composioSlug,
        instanceName,
        getToken
      );

      if (result.redirectUrl) {
        // Open OAuth in popup
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const safeUrlPattern = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
        if (!safeUrlPattern.test(result.redirectUrl)) return;
        const popup = window.open(
          result.redirectUrl,
          `${selectedApp.slug}OAuth`,
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        // Poll for popup close
        const pollInterval = setInterval(async () => {
          if (popup?.closed) {
            clearInterval(pollInterval);

            try {
              const statusResult = await checkComposioStatus(
                companyId,
                selectedApp.composioSlug,
                getToken
              );

              if (statusResult.connected) {
                const activeConnection = statusResult.connections?.find(
                  (c: any) => c.status === "ACTIVE"
                );

                if (activeConnection) {
                  setConnectedAccountId(activeConnection.id);
                  setIsConnected(true);
                } else {
                  setConnectionError(
                    "No active connection found. Please try again."
                  );
                }
              } else {
                setConnectionError(
                  "Connection not completed. Please try again."
                );
              }
            } catch (err: any) {
              setConnectionError(
                err.response?.data?.error || "Failed to verify connection"
              );
            }

            setConnecting(false);
          }
        }, 1000);

        // Check if popup blocked
        if (!popup || popup.closed) {
          clearInterval(pollInterval);
          setConnectionError(
            "Popup was blocked. Please allow popups and try again."
          );
          setConnecting(false);
        }
      }
    } catch (err: any) {
      setConnectionError(
        err.response?.data?.error || err.message || "Failed to start connection"
      );
      setConnecting(false);
    }
  }, [selectedApp, companyId, instanceName, getToken]);

  const handleApiKeyConnect = async () => {
    if (!selectedApp) return;

    // Check if we have dynamic fields or just a single API key
    const hasMultipleFields = authFields.length > 0;

    if (hasMultipleFields) {
      // Validate all required fields are filled
      const missingFields = authFields
        .filter((field) => field.required && !credentials[field.name]?.trim())
        .map((field) => field.displayName);

      if (missingFields.length > 0) {
        setConnectionError(`Please fill in: ${missingFields.join(", ")}`);
        return;
      }
    } else if (!apiKey.trim()) {
      setConnectionError("Please enter your API key");
      return;
    }

    setConnecting(true);
    setConnectionError("");

    try {
      const result = await connectComposioWithApiKey(
        companyId,
        instanceName,
        selectedApp.composioSlug,
        hasMultipleFields ? "" : apiKey, // Pass empty string if using credentials
        selectedApp.logo,
        selectedApp.description,
        getToken,
        hasMultipleFields ? credentials : undefined
      );

      if (result.success) {
        onSuccess();
        handleClose();
      } else {
        setConnectionError(result.error || "Failed to connect");
      }
    } catch (err: any) {
      setConnectionError(
        err.response?.data?.error ||
          err.message ||
          "Failed to connect with API key"
      );
    } finally {
      setConnecting(false);
    }
  };

  const handleInstall = async () => {
    if (!selectedApp || !connectedAccountId) return;

    setInstalling(true);
    try {
      await installGenericComposioIntegration(
        companyId,
        instanceName,
        selectedApp.slug,
        selectedApp.composioSlug,
        connectedAccountId,
        selectedApp.logo,
        selectedApp.description,
        getToken
      );
      onSuccess();
      handleClose();
    } catch (err: any) {
      onError(
        err.response?.data?.error ||
          err.message ||
          "Failed to install integration"
      );
    } finally {
      setInstalling(false);
    }
  };

  const handleClose = () => {
    setSelectedApp(null);
    setInstanceName("");
    setApiKey("");
    setCredentials({});
    setAuthFields([]);
    setIsConnected(false);
    setConnectedAccountId(null);
    setConnectionError("");
    setSearchQuery("");
    setDebouncedSearch("");
    onClose();
  };

  const renderAppGrid = () => {
    if (loading) {
      return (
        <Grid container spacing={2}>
          {[...Array(12)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  height: 160,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 1,
                  }}
                >
                  <Skeleton
                    variant="circular"
                    width={40}
                    height={40}
                    sx={{ flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" height={20} />
                  </Box>
                </Box>
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="90%" />
                <Skeleton variant="text" width="70%" />
              </Paper>
            </Grid>
          ))}
        </Grid>
      );
    }

    if (apps.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="text.secondary">
            {searchQuery
              ? "No apps found matching your search"
              : "No apps available"}
          </Typography>
        </Box>
      );
    }

    // Height for description section: 3 lines at ~1.4 line height
    const descriptionHeight = 60;

    return (
      <Grid container spacing={2}>
        {apps.map((app) => {
          const hasLongDescription =
            app.description && app.description.length > 120;
          const descriptionContent = app.description ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                lineHeight: 1.4,
                height: descriptionHeight,
              }}
            >
              {app.description}
            </Typography>
          ) : (
            <Box sx={{ height: descriptionHeight }} />
          );

          return (
            <Grid item xs={12} sm={6} md={4} key={app.slug}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  height: 160,
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: 1,
                  },
                }}
                onClick={() => handleSelectApp(app)}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 1,
                  }}
                >
                  <Avatar
                    src={app.logo}
                    alt={app.name}
                    sx={{ width: 40, height: 40, flexShrink: 0 }}
                  >
                    {app.name[0]}
                  </Avatar>
                  <Box sx={{ flex: 1, overflow: "hidden" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography variant="subtitle1" noWrap>
                        {app.name}
                      </Typography>
                      {app.authType === "api_key" && (
                        <KeyIcon
                          sx={{ fontSize: 14, color: "text.secondary" }}
                        />
                      )}
                    </Box>
                    {app.categories.length > 0 && (
                      <Box
                        sx={{ display: "flex", gap: 0.5, overflow: "hidden" }}
                      >
                        {app.categories.slice(0, 1).map((cat) => (
                          <Chip
                            key={cat}
                            label={cat}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", height: 20 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
                {hasLongDescription ? (
                  <Tooltip title={app.description} arrow placement="top">
                    {descriptionContent}
                  </Tooltip>
                ) : (
                  descriptionContent
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderAppInstall = () => {
    if (!selectedApp) return null;

    const isApiKeyOnly = selectedApp.authType === "api_key";
    const hasMultipleFields = authFields.length > 0;

    // Check if all required fields are filled
    const isFormValid = hasMultipleFields
      ? authFields.every((field) => !field.required || credentials[field.name]?.trim())
      : apiKey.trim();

    return (
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Avatar
            src={selectedApp.logo}
            alt={selectedApp.name}
            sx={{ width: 64, height: 64 }}
          >
            {selectedApp.name[0]}
          </Avatar>
          <Box>
            <Typography variant="h6">{selectedApp.name}</Typography>
            {selectedApp.description && (
              <Typography variant="body2" color="text.secondary">
                {selectedApp.description}
              </Typography>
            )}
          </Box>
        </Box>

        {connectionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {connectionError}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Integration Name"
          value={instanceName}
          onChange={(e) => setInstanceName(e.target.value)}
          sx={{ mb: 2 }}
          helperText="A name to identify this integration"
        />

        {isApiKeyOnly ? (
          // API Key flow
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              This integration requires credentials. You can get these from{" "}
              {selectedApp.name}'s dashboard or settings.
            </Alert>

            {loadingAuthFields ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : hasMultipleFields ? (
              // Dynamic fields based on auth requirements
              <>
                {authFields.map((field) => (
                  <TextField
                    key={field.name}
                    fullWidth
                    label={`${field.displayName}${field.required ? " *" : ""}`}
                    type={
                      field.name.toLowerCase().includes("key") ||
                      field.name.toLowerCase().includes("secret") ||
                      field.name.toLowerCase().includes("password") ||
                      field.name.toLowerCase().includes("token")
                        ? "password"
                        : "text"
                    }
                    value={credentials[field.name] || ""}
                    onChange={(e) =>
                      setCredentials((prev) => ({
                        ...prev,
                        [field.name]: e.target.value,
                      }))
                    }
                    sx={{ mb: 2 }}
                    helperText={field.description}
                  />
                ))}
              </>
            ) : (
              // Fallback to single API key field
              <TextField
                fullWidth
                label="API Key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                sx={{ mb: 3 }}
                helperText={`Enter your ${selectedApp.name} API key`}
              />
            )}

            <Button
              variant="contained"
              onClick={handleApiKeyConnect}
              disabled={connecting || !instanceName.trim() || !isFormValid || loadingAuthFields}
              startIcon={
                connecting && <CircularProgress size={16} color="inherit" />
              }
              fullWidth
              size="large"
            >
              {connecting ? "Connecting..." : "Connect"}
            </Button>
          </>
        ) : !isConnected ? (
          // OAuth flow
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Connect your {selectedApp.name} account to enable this
              integration. A popup will open for authorization.
            </Alert>
            <Button
              variant="contained"
              onClick={handleOAuthConnect}
              disabled={connecting || !instanceName.trim()}
              startIcon={
                connecting && <CircularProgress size={16} color="inherit" />
              }
              fullWidth
              size="large"
            >
              {connecting ? "Connecting..." : `Connect ${selectedApp.name}`}
            </Button>
          </>
        ) : (
          // OAuth connected, ready to install
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              {selectedApp.name} connected successfully! Click Install to save.
            </Alert>
            <Button
              variant="contained"
              onClick={handleInstall}
              disabled={installing}
              startIcon={
                installing && <CircularProgress size={16} color="inherit" />
              }
              fullWidth
              size="large"
            >
              {installing ? "Installing..." : "Install Integration"}
            </Button>
          </>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: "80vh" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {selectedApp ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="text"
              onClick={handleBack}
              sx={{ minWidth: "auto" }}
            >
              Back
            </Button>
            <Typography>/ Install {selectedApp.name}</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">{employeeText} Integration Tools</Typography>
            <Chip label="Beta" size="small" color="primary" />
          </Box>
        )}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {!selectedApp && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Give your {employeesText} access to these integrations. Browse and
              connect tools that can be enabled in your chat settings.
            </Typography>
            <TextField
              fullWidth
              placeholder="Search integrations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </>
        )}

        {selectedApp ? renderAppInstall() : renderAppGrid()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={connecting || installing}>
          {selectedApp ? "Cancel" : "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
